import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Modal } from "react-bootstrap";
import api from "services/api";
import ls from "services/ls";
import { __ } from "services/Translator";

// In-session reality-check reminder. Mounts once globally (next to
// InactivityChecker) so the session clock survives navigating between games.
// Interval comes from the RG engine (GET /rg/reality-check, band-bumped); the
// clock is client-side (localStorage), counted from the last acknowledgement to
// match AU/UK reality-check norms. Inert unless the engine reports enabled.
const START_KEY = "rg_session_start";
const ACK_KEY = "rg_session_last_ack";
const STALE_HOURS = 12;

export default function SessionRealityCheck() {
  const location = useLocation();
  const inPlay = location.pathname.split("/").includes("play");

  const [cfg, setCfg] = useState({ enabled: false, interval_minutes: 60 });
  const [show, setShow] = useState(false);
  const [elapsedMin, setElapsedMin] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    api
      .get("/rg/reality-check")
      .then((r) => {
        if (mounted && r.status === 200 && r.data && r.data.enabled) {
          setCfg(r.data);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  // Start (or resume) the session clock when the player enters a game. Reset a
  // stale clock so a long-abandoned session doesn't fire immediately.
  useEffect(() => {
    if (!inPlay) return;
    const start = ls.get(START_KEY);
    const stale = start && Date.now() - new Date(start).getTime() > STALE_HOURS * 3600000;
    if (!start || stale) {
      const now = new Date().toISOString();
      ls.set(START_KEY, now);
      ls.set(ACK_KEY, now);
    }
  }, [inPlay]);

  useEffect(() => {
    if (!cfg.enabled) {
      return undefined;
    }
    const tick = () => {
      if (!inPlay) return;
      const lastAck = ls.get(ACK_KEY);
      if (!lastAck) return;
      const minsSinceAck = (Date.now() - new Date(lastAck).getTime()) / 60000;
      if (minsSinceAck >= cfg.interval_minutes) {
        const start = ls.get(START_KEY);
        setElapsedMin(start ? Math.round((Date.now() - new Date(start).getTime()) / 60000) : Math.round(minsSinceAck));
        setShow(true);
      }
    };
    timerRef.current = setInterval(tick, 30000);
    return () => clearInterval(timerRef.current);
  }, [cfg, inPlay]);

  const acknowledge = async () => {
    ls.set(ACK_KEY, new Date().toISOString());
    setShow(false);
    try {
      await api.post("/rg/reality-check/ack", {});
    } catch (e) {}
  };

  if (!cfg.enabled || !show) {
    return null;
  }

  return (
    <Modal show={show} onHide={acknowledge} centered className="rg_reality_check_modal">
      <Modal.Body>
        <h4>{__("Reality check")}</h4>
        <p>
          {__("You have been playing for")} <b>{elapsedMin} {__("minutes")}</b>.
        </p>
        <p>
          <small>{__("Take a moment to consider whether you would like to keep playing.")}</small>
        </p>
        <div className="rg_rc_actions">
          <button type="button" className="commonBtn pTwoBtn" onClick={acknowledge}>
            {__("Continue playing")}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
